import {
  EIOSPublishType,
  EPushNoticationPublishType,
  IAppStoreConnectApiSetting,
  ICommonSettings,
  IImageLibraryDto
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IAppStoreConnectApiSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IAppStoreConnectApiSetting;
  publishType?: EIOSPublishType;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  p8FileName?: string;
  inAppPurchaseP8FileName?: string;
  appstorePublishType?: EPushNoticationPublishType;
}

export interface IAppStoreConnectApiSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingAppStoreConnectApiId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class AppStoreConnectApiSettings extends React.PureComponent<
  IAppStoreConnectApiSettingsProps,
  IAppStoreConnectApiSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IAppStoreConnectApiSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingConfig: undefined,
      publishType: undefined,
      allImage: [],
      imageSearchText: "",
      p8FileName: "",
      inAppPurchaseP8FileName: "",
      appstorePublishType: undefined
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IAppStoreConnectApiSettingsProps) {
    if (prevProps.editingAppStoreConnectApiId !== this.props.editingAppStoreConnectApiId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingAppStoreConnectApiId) {
      const setting = this.props.commonSettings.appStoreConnectApiSettings?.find(
        (x) => x.id === this.props.editingAppStoreConnectApiId && !x.isGlobalCommonConfigOnProjectConfig
      );

      this.setState({ editingConfig: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.name = setting.name;
          fieldsValue.issuerId = setting.issuerId;
          fieldsValue.appleId = setting.appleId;
          fieldsValue.appStoreConnectTeamId = setting.appStoreConnectTeamId;
          fieldsValue.developerTeamId = setting.developerTeamId;
          fieldsValue.publishType = setting.publishType;
          fieldsValue.appstorePublishType = setting.appstorePublishType;
          this.setState({ appstorePublishType: setting.appstorePublishType });
          this.setState({
            publishType: setting.publishType,
            p8FileName: setting.p8FileName
          });
          this.setState({
            publishType: setting.publishType,
            inAppPurchaseP8FileName: setting.inAppPurchaseP8FileName
          });
          if (setting.publishType === EIOSPublishType.Simulator)
            fieldsValue.appetizeApiToken = setting.appetizeApiToken;
          this.formRef.current?.setFieldsValue(fieldsValue);
        }
      });
    } else {
      this.setState(
        {
          editingConfig: undefined,
          publishType: undefined,
          p8FileName: undefined
        },
        () => {
          this.formRef.current?.resetFields();
        }
      );
      this.setState(
        {
          editingConfig: undefined,
          publishType: undefined,
          inAppPurchaseP8FileName: undefined
        },
        () => {
          this.formRef.current?.resetFields();
        }
      );
    }
  };

  existingNameValidator = async (_rule, value) => {
    const config = this.props.commonSettings?.appStoreConnectApiSettings;

    const isSettingAlreadyExist =
      config?.some(
        (x) =>
          x.name === value && x.id !== this.props.editingAppStoreConnectApiId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("App Store Settings Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    let obj: IAppStoreConnectApiSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      p8FileName: this.state.p8FileName,
      issuerId: data.issuerId?.trim(),
      appleId: data.appleId?.trim(),
      appStoreConnectTeamId: data.appStoreConnectTeamId?.trim(),
      developerTeamId: data.developerTeamId?.trim(),
      publishType: this.state.publishType,
      inAppPurchaseP8FileName: this.state.inAppPurchaseP8FileName,
      appetizeApiToken: data.appetizeApiToken?.trim(),
      appstorePublishType: this.state.appstorePublishType
    };
    if (this.props.editingAppStoreConnectApiId) {
      obj.id = this.props.editingAppStoreConnectApiId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "appStoreConnectApiSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "appStoreConnectApiSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingAppStoreConnectApiId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingAppStoreConnectApiId || "",
      configKey: "appStoreConnectApiSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`App Store setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="App Store Connect Api Setting"
        secondHeader="App Store Connect Api Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingAppStoreConnectApiId ? this.deleteSetting : undefined}
            deleteTestId="appStoreConnectModalDeleteButton"
            submitTestId={`appStoreConnectModal${this.props.editingAppStoreConnectApiId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingAppStoreConnectApiId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configAppStoreForm"
          />
        }
      >
        <Form
          id="configAppStoreForm"
          name="appStoreSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              "appstoreconnectapisettingpopup_appstoreconnectapisetting_description"
            )}
          >
            <ConfigField
              label="Name"
              required
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_name"
            >
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a App Store Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "App Store Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="appStoreConnectApiSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Issuer Id"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_issuerid"
            >
              <Form.Item
                name="issuerId"
                rules={[
                  {
                    required: false,
                    message: "Please enter a Issuer Id."
                  }
                ]}
              >
                <KuikaInput size="md" data-testid="appStoreConnectApiSettingIssuerIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Apple Id"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_appleid"
            >
              <Form.Item
                name="appleId"
                rules={[
                  {
                    required: false,
                    message: "Please enter a Apple Id."
                  }
                ]}
              >
                <KuikaInput size="md" data-testid="appStoreConnectApiSettingAppleIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Content Provider ID"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_teamid"
            >
              <Form.Item
                name="appStoreConnectTeamId"
                rules={[
                  {
                    required: false,
                    message: "Please enter a App Store Connect Team Id."
                  }
                ]}
              >
                <KuikaInput size="md" data-testid="appStoreConnectApiSettingContentProviderIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Team ID"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_developerteamid"
            >
              <Form.Item
                name="developerTeamId"
                rules={[
                  {
                    required: false,
                    message: "Please enter a Developer Team Id."
                  }
                ]}
              >
                <KuikaInput size="md" data-testid="appStoreConnectApiSettingTeamIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Publish type"
              required
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_publishtype"
            >
              <Form.Item
                name="publishType"
                rules={[
                  {
                    required: true,
                    message: "Please select a Publish Type."
                  }
                ]}
              >
                <KuikaSelectbox
                  size="md"
                  data-testid="appStoreConnectApiSettingPublishTypeDropdown"
                  defaultValue={this.state.publishType}
                  value={this.state.publishType}
                  onChange={(e) => this.setState({ publishType: e })}
                  options={[
                    {
                      value: EIOSPublishType.Simulator,
                      label: "Appetize",
                      testId: "appetizeOption",
                      searchLabel: "Appetize"
                    },
                    {
                      value: EIOSPublishType.TestFlight,
                      label: "TestFlight",
                      testId: "testflightOption",
                      searchLabel: "TestFlight"
                    },
                    { value: EIOSPublishType.Ipa, label: "ipa", testId: "ipaOption", searchLabel: "ipa" }
                  ]}
                />
              </Form.Item>
            </ConfigField>
            {this.state.publishType && this.state.publishType === EIOSPublishType.Simulator ? (
              <ConfigField
                label="Appetize Api token"
                tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_appetizeapitoken"
              >
                <Form.Item
                  name="appetizeApiToken"
                  rules={[
                    {
                      required: false && true,
                      message: "Please enter a Appetize Api Token."
                    }
                  ]}
                >
                  <KuikaInput size="md" data-testid="appetizeApiTokenInput" />
                </Form.Item>
              </ConfigField>
            ) : (
              <></>
            )}

            <ConfigField
              label="Environment Type"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_appstorePublishType"
            >
              <Form.Item name="appstorePublishType">
                <KuikaSelectbox
                  size="md"
                  defaultValue={this.state.appstorePublishType}
                  value={this.state.appstorePublishType}
                  onChange={(e) => this.setState({ appstorePublishType: e })}
                  options={[
                    { value: EPushNoticationPublishType.Development, label: "Development" },
                    { value: EPushNoticationPublishType.Production, label: "Production" }
                  ]}
                />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Connect Certificate"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_notificationcertificate"
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".p8"
                getImageLibrary={this.getImageLibrary}
                selectedId={this.state.p8FileName}
                fileName={this.state.p8FileName}
                onSelect={(image: IImageLibraryDto): void => this.setState({ p8FileName: image.fileName })}
                onClear={(): void => this.setState({ p8FileName: undefined })}
                chooseTestId="certificateChooseReplaceButton"
              />
            </ConfigField>

            <ConfigField
              label="In App Purchase Connect Certificate"
              tooltipContentKey="appstoreconnectapisettingpopup_appstoreconnectapisetting_notificationcertificate"
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".p8"
                getImageLibrary={this.getImageLibrary}
                selectedId={this.state.inAppPurchaseP8FileName}
                fileName={this.state.inAppPurchaseP8FileName}
                onSelect={(image: IImageLibraryDto): void => this.setState({ inAppPurchaseP8FileName: image.fileName })}
                onClear={(): void => this.setState({ inAppPurchaseP8FileName: undefined })}
                chooseTestId="certificateChooseReplaceButton"
              />
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
